import { setHeader, setPipelineFilters } from 'actions'
import { PipeLineType } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

// Top-header overrides shown only while the app is in Servicing mode.
const servicingHeader = {
  // Reports is one tabbed page for both modes, so servicing no longer renames or redirects the
  // top-level entry; null here also clears an override left in localStorage by an older build.
  reportsOverride: null,
  // `permission` is the same one the entry's route is gated on in `protected.route.tsx`. Without
  // it the link showed for everybody and dead-ended on the route's permission screen, so a
  // servicer who cannot work collections or the foreclosure book was still offered both.
  servicingMenu: [
    { key: 'servicingAch', name: 'ACH', href: '/servicing-ach', permission: 'Servicing.ACH' },
    {
      key: 'debitRequests',
      name: 'Debit Requests',
      href: '/debit-requests',
      permission: 'Servicing.ACH',
    },
    { key: 'paymentChecks', name: 'Check Queue', href: '/payment-checks', permission: 'Servicing.CheckQueue' },
    {
      key: 'servicingStatements',
      name: 'Statements',
      href: '/servicing-statements',
      permission: 'Servicing.Statements',
    },
    {
      key: 'servicingTaxForms',
      name: 'Tax Forms',
      href: '/servicing-tax-forms',
      // Shares the Statements permission on purpose: it is the same job (producing a borrower
      // document for the whole book and sending it out), and a new permission would be absent on
      // every existing tenant until someone granted it, which is how a shipped screen stays
      // invisible.
      permission: 'Servicing.Statements',
    },
    {
      key: 'taxFiling',
      name: 'Tax Filing',
      href: '/tax-filing',
      // The IRS side of the same job: what gets reported, reviewed and transmitted, as against
      // Tax Forms above, which is the borrower's copy. Same permission, same reasoning.
      permission: 'Servicing.Statements',
    },
    {
      key: 'taxFilers',
      name: 'Tax Filers',
      href: '/tax-filers',
      permission: 'Servicing.Statements',
    },
    {
      key: 'onlinePayments',
      name: 'Online Payments',
      href: '/online-payments',
      permission: 'Servicing.OnlinePayments',
    },
    { key: 'paymentImport', name: 'Payment Import', href: '/payment-import', permission: 'Servicing.PaymentImport' },
    { key: 'nsfImport', name: 'NSF Import', href: '/nsf-import', permission: 'Servicing.PaymentImport' },
    {
      key: 'paymentDefaults',
      name: 'Payment Defaults',
      href: '/payment-defaults',
      permission: 'Servicing.PaymentDefaults',
    },
    {
      key: 'armAdjustments',
      name: 'ARM Adjustments',
      href: '/arm-adjustments',
      permission: 'Servicing.ArmAdjustments',
    },
    {
      key: 'printMail',
      name: 'Print & Mail',
      href: '/print-mail',
      // Its own permission rather than Statements': origination correspondence and year-end tax
      // forms go through the same queue, so it is not a servicing screen. It defaults to whoever
      // runs statements (see `permissions.constants` on the backend), so it is not invisible on a
      // tenant nobody has granted it to.
      permission: 'PrintMail',
    },
    { key: 'collections', name: 'Collections', href: '/collections', permission: 'Servicing.Collections' },
    { key: 'extensions', name: 'Extensions', href: '/extensions', permission: 'Servicing.Extensions' },
    { key: 'foreclosure', name: 'Foreclosure', href: '/foreclosure', permission: 'Servicing.Foreclosure' },
    {
      key: 'creditReporting',
      name: 'Credit Reporting',
      href: '/credit-reporting',
      permission: 'Servicing.CreditReporting',
    },
    {
      key: 'servicingTextMessages',
      name: 'Text Messages',
      href: '/servicing-text-messages',
      permission: 'Servicing.TextMessages',
    },
  ],
}
const originationHeader = { reportsOverride: null, servicingMenu: null }

// Routes that mean "the user is now working in servicing" and "now in origination".
// Anything not listed here leaves the current view alone - admin pages and the loan
// screens shared by both modes (notes, tasks, budget) must not flip it.
const servicingPaths = [
  '/servicing/',
  '/servicing-ach',
  '/debit-requests',
  '/servicing-statements',
  '/servicing-tax-forms',
  '/tax-filing',
  '/tax-filers',
  '/payment-checks',
  '/payment-import',
  '/nsf-import',
  '/payment-defaults',
  '/arm-adjustments',
  '/print-mail',
  '/online-payments',
  '/collections',
  '/extensions',
  '/foreclosure',
  '/credit-reporting',
  '/servicing-text-messages',
  '/reports/servicing',
  '/configurablePage/servicing/',
]
const originationPaths = ['/loan_process/']

/** Which mode a route belongs to, or null when the route is shared by both. */
export const viewForPath = (pathname: string) => {
  if (servicingPaths.some((path) => pathname.startsWith(path))) return PipeLineType.Servicing
  if (originationPaths.some((path) => pathname.startsWith(path))) return PipeLineType.Default
  return null
}

/**
 * Keeps the pipeline view in step with where the user actually goes: opening a loan in
 * servicing switches the view (and the top header) to Servicing, and going back into an
 * origination loan switches it back.
 */
export const usePipelineViewSync = () => {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const { hasPermission } = usePermissions()
  // Selected one field at a time: this runs at the app root, so it must not re-render on
  // every unrelated store change.
  const pipelineType = useSelector((state: any) => state.pipelineFilters.pipelineType)
  const reportsOverride = useSelector((state: any) => state.header?.reportsOverride ?? null)
  const servicingMenu = useSelector((state: any) => state.header?.servicingMenu ?? null)

  // Only users who could switch the view themselves get switched automatically -
  // otherwise a restricted user could be moved into a view with no way back.
  const canChangePipelineType = hasPermission('Pipeline.View.PipelineType')

  useEffect(() => {
    if (!canChangePipelineType) return

    const nextView = viewForPath(pathname)
    if (!nextView || nextView === pipelineType) return
    // Origination only takes over from Servicing, so an FCI view is left as it is.
    if (nextView === PipeLineType.Default && pipelineType !== PipeLineType.Servicing) return

    // Drop the filters that belong to the mode we are leaving; the pipeline refills them
    // with the new mode's defaults the next time it loads.
    dispatch(
      setPipelineFilters({ pipelineType: nextView, status: {}, orderBy: 'asOf', lock: '0', maturity: '0' }),
    )
  }, [pathname, pipelineType, canChangePipelineType])

  // Keep the top header in step with the view, wherever the switch happened.
  useEffect(() => {
    if (!pipelineType) return
    const target = pipelineType === PipeLineType.Servicing ? servicingHeader : originationHeader
    if (JSON.stringify({ reportsOverride, servicingMenu }) === JSON.stringify(target)) return
    dispatch(setHeader(target))
  }, [pipelineType, reportsOverride, servicingMenu])
}
